import { computed, type Ref } from 'vue'

import { Seasons } from '@collector/sportsapi-client'
import { sportsApiClient } from '@desktop/globalState/sportsApiClient'
import { useEventId } from '@desktop/views/Relation/composables/useEventId'
import {
  queryOptions,
  useQuery,
  type UndefinedInitialQueryOptionsWithDataTag,
  type UseQueryReturnType,
} from '@tanstack/vue-query'

import {
  useCompetitionQuery,
  type CompetitionData,
} from './useCompetitionQuery'

type SeasonDetailsQueryKey = readonly ['event', 'seasonDetails', Ref<number>]

type SeasonDetailsQueryOptions = UndefinedInitialQueryOptionsWithDataTag<
  Seasons.Details.SeasonDetail[],
  Error,
  Seasons.Details.SeasonDetail[],
  SeasonDetailsQueryKey
>

export function seasonDetailsQueryOptions(
  eventId: Ref<number>,
  competitionData: Ref<CompetitionData | undefined>,
): SeasonDetailsQueryOptions {
  return queryOptions({
    queryKey: ['event', 'seasonDetails', eventId] as const,
    enabled: computed(() => !!competitionData.value),
    queryFn: async (): Promise<Seasons.Details.SeasonDetail[]> => {
      const competition = competitionData.value?.competition
      if (!competition) {
        throw new Error(
          'Competition query data missing for season details fetch',
        )
      }
      const response = await sportsApiClient.Seasons.Details.getBySeasonId(
        competition.season.id,
      )

      return response.api.data
    },
  })
}

export const useSeasonDetailsQuery = (
  options?: Omit<SeasonDetailsQueryOptions, 'queryKey' | 'queryFn'>,
): UseQueryReturnType<Seasons.Details.SeasonDetail[], Error> => {
  const eventId = useEventId()
  const { data: competitionData } = useCompetitionQuery()

  return useQuery<
    Seasons.Details.SeasonDetail[],
    Error,
    Seasons.Details.SeasonDetail[],
    SeasonDetailsQueryKey
  >({
    ...seasonDetailsQueryOptions(eventId, competitionData),
    ...options,
  })
}
